import { useCallback } from 'react'
import { parseAsBoolean, parseAsString, useQueryState } from 'nuqs'
import { isValidFareClassWithReward, parseFareClassFromUrl, toBaseFareClass, type FareClassWithReward, type FlightsClassTypes } from 'constants/flight'

/**
 * Shared URL state for Maldives charter filter params. Centralises the query-state
 * reads/writes that were previously duplicated across 6+ offer-page and search-page files.
 */
export function useCharterFilterUrlState() {
  const [fareClassUrlRaw, setFareClassUrl] = useQueryState('fareClass', parseAsString)
  const [selectedCabinRaw, setSelectedCabinRaw] = useQueryState('selectedCabin', parseAsString)
  const [rewardSeatsOnly, setRewardSeatsOnlyRaw] = useQueryState('societeRewards', parseAsBoolean.withDefault(false))
  const [infiniteMemberOnly, setInfiniteMemberOnlyRaw] = useQueryState('infiniteMember', parseAsBoolean.withDefault(false))

  const fareClassFromUrl = fareClassUrlRaw ? parseFareClassFromUrl(fareClassUrlRaw) : undefined
  const cabinFilter: FlightsClassTypes | null = selectedCabinRaw && isValidFareClassWithReward(selectedCabinRaw)
    ? toBaseFareClass(selectedCabinRaw)
    : null

  /** Sets cabin filter URL param, normalising reward variants to base cabin. */
  const setCabinFilter = useCallback((fc: FareClassWithReward | null) => {
    setSelectedCabinRaw(fc ? toBaseFareClass(fc) : null)
  }, [setSelectedCabinRaw])

  // The "Société Rewards" and "Infinite Member Price" filters are mutually exclusive — the
  // Infinite member rate only applies to non-reward (cash) fares — so enabling one clears the
  // other (per product decision on LOYAL-2563).
  const setRewardSeatsOnly = useCallback((enabled: boolean) => {
    setRewardSeatsOnlyRaw(enabled)
    if (enabled) {
      setInfiniteMemberOnlyRaw(false)
    }
  }, [setRewardSeatsOnlyRaw, setInfiniteMemberOnlyRaw])

  const setInfiniteMemberOnly = useCallback((enabled: boolean) => {
    setInfiniteMemberOnlyRaw(enabled)
    if (enabled) {
      setRewardSeatsOnlyRaw(false)
    }
  }, [setInfiniteMemberOnlyRaw, setRewardSeatsOnlyRaw])

  return {
    fareClassUrlRaw,
    fareClassFromUrl,
    setFareClassUrl,
    cabinFilter,
    setCabinFilter,
    rewardSeatsOnly,
    setRewardSeatsOnly,
    infiniteMemberOnly,
    setInfiniteMemberOnly,
  }
}
